
import { AuditModel, AuditShiftTypeModel, PermissionTypeEnum, QueryOperator, TenantUserModel, ShiftTypeModel } from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import Page from '@/Page.vue';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import { auditsStore } from '../+state/store';
import { Guid } from '@/libs/common/functions/guid';
import { cloneDeep, isEqual } from 'lodash';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import ShiftTypeSelect from '@/libs/common/components-smart/ShiftTypeSelect.vue';
import { shiftTypesStore } from '@/libs/shift-types/+state/store';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { auditShiftTypesStore } from '@/libs/audit-shift-types/+state/store';

@Component({
    components: { ShiftTypeSelect },
})
export default class Plan extends Page {
    @Prop() tenantId!: string;
    @Prop() auditId!: string;
    districtId: string | null = null;
    audit: AuditModel | null = null;
    auditOriginal: AuditModel | null = null;
    basicRules = [(v: any) => !!v || this.$t('validation.thisFieldIsRequired')];
    deleteDialog = false;
    deleting = false;
    valid = false;
    loading = true;
    saving = false;
    hasChanges = false;
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
    auditShiftTypes: { shiftTypeId: string; auditShiftType: AuditShiftTypeModel | null | undefined; shiftType: ShiftTypeModel | null | undefined }[] = [];
    selectedShiftTypes: {
        shiftTypeId: string;
        auditShiftType: AuditShiftTypeModel | null | undefined;
        shiftType: ShiftTypeModel | null | undefined;
    }[] = [];
    selectedShiftTypesOriginal: {
        shiftTypeId: string;
        auditShiftType: AuditShiftTypeModel | null | undefined;
        shiftType: ShiftTypeModel | null | undefined;
    }[] = [];
    tableHeaders = [{ text: this.$i18n.t('shiftTypes.name'), value: 'shiftType.name', align: 'left', sortable: false }];

    @Ref() form!: any;

    // ✅ NOVÝ SYSTÉM: Kontroluje, zda je audit readonly podle kategorie-specifických oprávnění
    get isReadonly(): boolean {
        if (!this.audit || !this.audit.categoryId) return true;
        
        // Kontrola EditAllAudits s kontextovými oprávněními
        if (this.hasAccessToCategory(this.audit.categoryId, [PermissionTypeEnum.EditAllAudits])) {
            return false;
        }

        // Kontrola EditOwnedAudit s kontextovými oprávněními (musí být owner)
        if (this.audit.ownerId === this.tenantUser.id && 
            this.hasAccessToCategory(this.audit.categoryId, [PermissionTypeEnum.EditOwnedAudit])) {
            return false;
        }

        return true;
    }

    @Watch('audit', { deep: true })
    @Watch('selectedShiftTypes', { deep: true })
    questionGroupsChanged() {
        this.hasChanges = !isEqual(this.audit, this.auditOriginal);
        if (!this.hasChanges) {
            this.hasChanges = !isEqual(this.selectedShiftTypes, this.selectedShiftTypesOriginal);
        }
    }

    mounted() {
        this.audit = { ...auditsStore.useGetter(CrudGetter.Detail) } as AuditModel;
        this.auditOriginal = cloneDeep(this.audit);
        this.loadItems().then();
        this.loading = false;
    }

    public getAuditShiftTypes(): Promise<AuditShiftTypeModel[]> {
        return new Promise<AuditShiftTypeModel[]>((resolve, reject) => {
            auditShiftTypesStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload(undefined, [{ field: 'auditId', op: QueryOperator.Eq, comparand: this.auditId }])
            );
            this.subscribe(auditShiftTypesStore, CrudReponse.GetAll)
                .then((existingAuditShiftTypes: AuditShiftTypeModel[]) => {
                    resolve(existingAuditShiftTypes);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    public getShiftTypes(): Promise<AuditShiftTypeModel[]> {
        return new Promise<AuditShiftTypeModel[]>((resolve, reject) => {
            shiftTypesStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload({
                    field: 'name',
                    index: 1,
                    order: 'asc',
                })
            );
            this.subscribe(shiftTypesStore, CrudReponse.GetAll)
                .then((existingAuditShiftTypes: ShiftTypeModel[]) => {
                    resolve(existingAuditShiftTypes);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    public deleteAllPromise(payload: AuditShiftTypeModel[]): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.deleteAllPromisePop(payload, resolve, reject);
        });
    }

    public saveAllPromise(payload: AuditShiftTypeModel[]): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.saveAllPromisePop(payload, resolve, reject);
        });
    }

    private save() {
        this.saving = true;
        auditsStore.dispatch(CrudAction.Update, { item: this.audit });
        this.subscribe(auditsStore, CrudReponse.Update)
            .then((e: AuditModel) => {
                this.audit = e;
                this.getAuditShiftTypes()
                    .then((existingAuditShiftTypes: AuditShiftTypeModel[]) => {
                        let selectedShiftTypes = this.selectedShiftTypes;
                        if (!this.audit!.useShiftTypeLimitation) {
                            selectedShiftTypes = [];
                        }
                        const auditShiftTypesToDelete = existingAuditShiftTypes.filter(
                            x => selectedShiftTypes.find(n => n.shiftTypeId == x.shiftTypeId) == null
                        );
                        const auditShiftTypesToInsert = selectedShiftTypes
                            .filter(x => existingAuditShiftTypes.find(n => n.shiftTypeId == x.shiftTypeId) == null)
                            .map(x => {
                                return {
                                    id: x.auditShiftType?.id ?? Guid.EmptyGuid(),
                                    auditId: this.auditId,
                                    shiftTypeId: x.shiftTypeId,
                                } as AuditShiftTypeModel;
                            });
                        this.deleteAllPromise(auditShiftTypesToDelete)
                            .then(() => {
                                this.saveAllPromise(auditShiftTypesToInsert)
                                    .then(() => {
                                        this.loadItems()
                                            .then(() => {
                                                this.saving = false;
                                            })
                                            .catch((e: any) => {
                                                this.saving = false;
                                                console.error(e);
                                            });
                                    })
                                    .catch((e: any) => {
                                        this.saving = false;
                                        console.error(e);
                                    });
                            })
                            .catch((e: any) => {
                                this.saving = false;
                                console.error(e);
                            });
                    })
                    .catch((e: any) => {
                        this.saving = false;
                        console.error(e);
                    });
            })
            .catch((e: any) => {
                this.saving = false;
                console.error(e);
            });
    }

    private loadItems(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.getAuditShiftTypes()
                .then((existingAuditShiftTypes: AuditShiftTypeModel[]) => {
                    this.getShiftTypes()
                        .then((shiftTypes: ShiftTypeModel[]) => {
                            const deletedShiftTypes = existingAuditShiftTypes!.filter(x => shiftTypes.find(n => n.id == x.shiftTypeId) == null);
                            this.auditShiftTypes = [
                                ...deletedShiftTypes.map(x => {
                                    return {
                                        shiftTypeId: x.shiftTypeId!,
                                        auditShiftType: x,
                                        shiftType: x.shiftType,
                                    };
                                }),
                            ];
                            this.selectedShiftTypes = [
                                ...deletedShiftTypes.map(x => {
                                    return {
                                        shiftTypeId: x.shiftTypeId!,
                                        auditShiftType: null,
                                        shiftType: null,
                                    };
                                }),
                            ];
                            this.auditShiftTypes = [
                                ...this.auditShiftTypes,
                                ...shiftTypes.map(x => {
                                    return {
                                        shiftTypeId: x.id!,
                                        auditShiftType: existingAuditShiftTypes!.find(n => n.shiftTypeId == x.id) ?? {
                                            id: Guid.EmptyGuid(),
                                        },
                                        shiftType: x,
                                    };
                                }),
                            ];
                            this.selectedShiftTypes = [
                                ...existingAuditShiftTypes!.map(x => {
                                    return {
                                        shiftTypeId: x.shiftTypeId!,
                                        auditShiftType: null,
                                        shiftType: null,
                                    };
                                }),
                            ];
                            this.selectedShiftTypesOriginal = [...this.selectedShiftTypes];
                            resolve(undefined);
                        })
                        .catch((e: any) => reject(e));
                })
                .catch((e: any) => reject(e));
        });
    }

    private deleteAllPromisePop(payload: AuditShiftTypeModel[], resolve: (value: any) => void, reject: (reason?: any) => void) {
        if (payload.length > 0) {
            const payloadItem = payload[0];
            if (payloadItem.id != Guid.EmptyGuid()) {
                auditShiftTypesStore.dispatch(CrudAction.Delete, { id: payloadItem.id });
                this.subscribe(auditShiftTypesStore, CrudReponse.Delete)
                    .then((e: any) => {
                        payload.shift();
                        this.deleteAllPromisePop(payload, resolve, reject);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            }
        } else {
            resolve(null);
        }
    }

    private saveAllPromisePop(payload: AuditShiftTypeModel[], resolve: (value: any) => void, reject: (reason?: any) => void) {
        if (payload.length > 0) {
            const payloadItem = payload[0];
            if (payloadItem.id == Guid.EmptyGuid()) {
                auditShiftTypesStore.dispatch(CrudAction.Create, { item: payloadItem });
                this.subscribe(auditShiftTypesStore, CrudReponse.Create)
                    .then((e: any) => {
                        payload.shift();
                        this.saveAllPromisePop(payload, resolve, reject);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            } else {
                auditShiftTypesStore.dispatch(CrudAction.Update, { item: payloadItem });
                this.subscribe(auditShiftTypesStore, CrudReponse.Update)
                    .then((e: any) => {
                        payload.shift();
                        this.saveAllPromisePop(payload, resolve, reject);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            }
        } else {
            resolve(null);
        }
    }
}
